import type { BreakdownItem } from '@idpe/schema';
import { createFileRoute } from '@tanstack/react-router';

import { FiltrosBar } from '~/components/analytics/FiltrosBar.js';
import { KpiCard } from '~/components/analytics/KpiCard.js';
import { TopList } from '~/components/analytics/TopList.js';
import { type Column, DataTable } from '~/components/ui/DataTable.js';
import { useBreakdown, useConversoes } from '~/features/analytics/hooks.js';
import { analyticsSearchSchema, searchToPeriod } from '~/features/analytics/useFiltros.js';
import { downloadCsv, n } from '~/lib/csv.js';

export const Route = createFileRoute('/_auth/analytics/conteudo')({
  validateSearch: (search) => analyticsSearchSchema.parse(search),
  component: AnalyticsConteudo,
});

function AnalyticsConteudo() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const period = searchToPeriod(search);
  const setRange = (range: '7d' | '30d' | '90d') =>
    void navigate({ search: { ...search, range, from: undefined, to: undefined } });
  const toggleComparar = () => void navigate({ search: { ...search, comparar: !period.comparar } });
  const topPages = useBreakdown(period, 'page', 25);
  const topCategorias = useBreakdown(period, 'category', 6);
  const conversoes = useConversoes(period);

  // Filtra páginas de publicações
  const articles = (topPages.data?.items ?? []).filter((p) => p.key.startsWith('/publicacoes/'));

  const articleReads = conversoes.data?.customEvents.find((e) => e.name === 'article_read');

  function handleExport() {
    if (articles.length === 0) return;
    downloadCsv(`analytics-artigos-${period.range}`, articles, [
      { key: 'name', label: 'Artigo' },
      { key: 'visitors', label: 'Visitantes' },
      { key: 'pageviews', label: 'Pageviews' },
    ]);
  }

  const columns: Column<BreakdownItem>[] = [
    {
      key: 'name',
      header: 'Artigo',
      cell: (row) => (
        <a href={row.key} target="_blank" rel="noopener noreferrer">
          <code className="analytics-top__path">{row.name}</code>
        </a>
      ),
      mobileLine: 'primary',
    },
    {
      key: 'visitors',
      header: 'Visitantes',
      cell: (row) => n(row.visitors),
      align: 'right',
      mobileLine: 'meta',
    },
    {
      key: 'pageviews',
      header: 'Pageviews',
      cell: (row) => (row.pageviews != null ? n(row.pageviews) : '—'),
      align: 'right',
      mobileLine: 'meta',
    },
  ];

  return (
    <div className="analytics-page">
      <FiltrosBar
        period={period}
        onRangeChange={setRange}
        onToggleComparar={toggleComparar}
        onExportCsv={handleExport}
      />

      <section className="analytics-kpi-grid" aria-label="Conteúdo no período">
        <KpiCard
          label="Páginas de artigo (visitantes)"
          value={n(articles.reduce((s, a) => s + a.visitors, 0))}
        />
        <KpiCard
          label="Leituras completas (≥75%)"
          value={n(articleReads?.count ?? 0)}
          hint="Evento `article_read` (Plausible custom)"
        />
        <KpiCard label="Categorias com tráfego" value={n(topCategorias.data?.items.length ?? 0)} />
      </section>

      <section className="analytics-block">
        <header className="analytics-block__head">
          <h2 className="analytics-block__title">Top artigos</h2>
        </header>
        {articles.length === 0 ? (
          <p className="analytics-empty">
            Sem visitas em páginas <code>/publicacoes/...</code> no período.
          </p>
        ) : (
          <DataTable
            data={articles}
            columns={columns}
            keyExtractor={(r) => r.key}
            emptyMessage="Sem artigos com tráfego."
          />
        )}
      </section>

      <section className="analytics-grid-2">
        <TopList
          title="Top categorias"
          items={topCategorias.data?.items ?? []}
          emptyMessage="Categorias custom property será preenchida quando o site enviar `event:props:categoria` ao Plausible (Fase 9)."
        />
      </section>
    </div>
  );
}
